import {
  hashKey,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AiReviewStatus,
  getV1CompaniesCompanyIdOrdersDispatch,
  GetV1CompaniesCompanyIdOrdersDispatchData,
  InvoiceState,
  JobState,
  OrderState,
  SiteType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isEqual, noop, omit } from 'lodash-es';
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import { FetchReason } from '~constants/enums';
import { Order } from '~hooks/useOrders/models';
import { extractPagination, Pagination } from '~services/pagination';
import { Nullable } from '~types/Nullable';
import {
  persistDateRange,
  retrievePersistedDateRange,
} from '~utils/dateRangePersistence';
import { approvalFilterToBoolean } from '~utils/filters/approval-filter-utils';

import { FilesQueryOrdersSort, JOBS_QUERY_CONTEXT_PREFIX } from './constants';
import {
  getEffectiveJobStates,
  makeFilesQueryContextKey,
  normalizeJobStateSelection,
} from './utils';

enum StorageKeys {
  // Explicit nulls mean "All dates".
  APPLIED_DATE_FILTERS = 'approveJobs_appliedDateFilters',
}

const makeStorageKey = (key: StorageKeys, storageNamespace?: string) =>
  storageNamespace ? `${key}_${storageNamespace}` : key;

// Mirrors OrdersDispatchStore.hasActiveNonSearchFilters: any set filter except the
// search term counts — dates included, since clearing resets them too.
function hasActiveNonSearchFilters(filters: FilesQueryFilters) {
  const { searchQuery: _searchQuery, ...rest } = filters;

  return Object.values(rest).some(
    (value) => value !== undefined && !(Array.isArray(value) && value.length === 0),
  );
}

const derivePaginationQueryParams = (pageParam?: Pagination) => {
  return {
    'page[limit]': 20,
    'page[after]': pageParam?.after,
  };
};

export const deriveFilterQueryParams = (filters: FilesQueryFilters) => {
  const {
    aiReviewStatuses,
    customerAccountIds,
    departmentIds,
    dispatchNumberIds,
    driverIds,
    dropoffSiteIds,
    endDate,
    equipmentOwnerCompanyIds,
    equipmentTypeIds,
    foremanIds,
    jobApprovalStatus,
    jobStates,
    loadApprovalStatus,
    materialIds,
    orderStates,
    pickupSiteIds,
    prevailingWage,
    projectExternalIds,
    projectIds,
    requesterIds,
    searchQuery,
    siteTypes,
    startDate,
    ticketPotentialDuplicate,
    ticketTypes,
    vendorAccountIds,
    payablesStates,
    receivablesStates,
  } = filters;

  const query: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = {
    'filter[department_ids]': departmentIds?.length ? departmentIds : undefined,

    'filter[equipment_type_ids]': equipmentTypeIds?.length ? equipmentTypeIds : undefined,

    'filter[job][ai_review][statuses]': aiReviewStatuses?.length
      ? aiReviewStatuses
      : undefined,

    // A job with no driver and no vendor assignment is work nobody was asked to do, so
    // a biller can never action it. Always narrowed out — there is nothing to toggle.
    'filter[job][assigned]': true,

    'filter[job][billing_approved]': approvalFilterToBoolean(jobApprovalStatus),

    'filter[job][customer_account_ids]': customerAccountIds?.length
      ? customerAccountIds
      : undefined,

    'filter[dispatch_number_ids]': dispatchNumberIds?.length
      ? dispatchNumberIds
      : undefined,

    'filter[job][driver_ids]': driverIds?.length ? driverIds : undefined,

    'filter[job][dropoff_site_ids]': dropoffSiteIds?.length ? dropoffSiteIds : undefined,

    'filter[job][external_ids]': projectExternalIds?.length
      ? projectExternalIds
      : undefined,

    'filter[job][end_date]': endDate
      ? dayjs.tz(endDate).endOf('day').toISOString()
      : undefined,

    'filter[job][foreman_ids]': foremanIds?.length ? foremanIds : undefined,

    'filter[job][load][billing_approved]': approvalFilterToBoolean(loadApprovalStatus),

    'filter[job][material_ids]': materialIds?.length ? materialIds : undefined,

    'filter[job][pickup_site_ids]': pickupSiteIds?.length ? pickupSiteIds : undefined,

    'filter[job][project_ids]': projectIds?.length ? projectIds : undefined,

    'filter[job][start_date]': startDate
      ? dayjs.tz(startDate).startOf('day').toISOString()
      : undefined,

    'filter[job][states]': getEffectiveJobStates(jobStates),

    'filter[job][site_types]': siteTypes?.length ? siteTypes : undefined,

    'filter[job][ticket][potential_duplicate]': ticketPotentialDuplicate?.length
      ? ticketPotentialDuplicate[0] === 'true'
      : undefined,

    ...(prevailingWage?.length
      ? { 'filter[job][prevailing_wage]': prevailingWage[0] === 'true' }
      : {}),

    'filter[default_digital_ticket_view]':
      ticketTypes?.length === 1 ? ticketTypes[0] === 'true' : undefined,

    'filter[job][vendor_account_ids]': vendorAccountIds?.length
      ? vendorAccountIds
      : undefined,

    'filter[job][equipment][company_ids]': equipmentOwnerCompanyIds?.length
      ? equipmentOwnerCompanyIds
      : undefined,

    'filter[requester_ids]': requesterIds?.length ? requesterIds : undefined,
    'filter[job][payable_invoice][states]': payablesStates?.length
      ? payablesStates
      : undefined,
    'filter[job][receivable_invoice][states]': receivablesStates?.length
      ? receivablesStates
      : undefined,

    'filter[states]': orderStates?.length ? orderStates : undefined,

    'search[job][datagrid]': searchQuery || undefined,
  };

  return query;
};

const deriveSortQueryParam = (orderSort: OrderOption<FilesQueryOrdersSort>) => {
  const { orderBy, sortBy } = orderSort;

  return {
    'sort[account][name]':
      orderBy === FilesQueryOrdersSort.ACCOUNT_NAME ? sortBy : undefined,

    'sort[company][legal_name]':
      orderBy === FilesQueryOrdersSort.COMPANY_LEGAL_NAME ? sortBy : undefined,
    'sort[created_at]': orderBy === FilesQueryOrdersSort.CREATED_AT ? sortBy : undefined,

    'sort[dispatch_number][name]':
      orderBy === FilesQueryOrdersSort.DISPATCH_NUMBER_NAME ? sortBy : undefined,

    'sort[friendly_order_id]':
      orderBy === FilesQueryOrdersSort.FRIENDLY_ORDER_ID ? sortBy : undefined,

    'sort[load_at]': orderBy === FilesQueryOrdersSort.LOAD_AT ? sortBy : undefined,
  };
};

export type FilesQueryFilters = {
  aiReviewStatuses?: AiReviewStatus[];
  customerAccountIds?: string[];
  departmentIds?: string[];
  dispatchNumberIds?: string[];
  driverIds?: string[];
  dropoffSiteIds?: string[];
  endDate?: Dayjs;
  equipmentOwnerCompanyIds?: string[];
  equipmentTypeIds?: string[];
  foremanIds?: string[];
  jobApprovalStatus?: string[];
  jobStates?: JobState[];
  loadApprovalStatus?: string[];
  materialIds?: string[];
  orderStates?: OrderState[];
  pickupSiteIds?: string[];
  prevailingWage?: string[];
  projectExternalIds?: string[];
  projectIds?: string[];
  requesterIds?: string[];
  searchQuery?: string;
  siteTypes?: SiteType[];
  startDate?: Dayjs;
  ticketPotentialDuplicate?: string[];
  ticketTypes?: string[];
  vendorAccountIds?: string[];
  payablesStates?: InvoiceState[];
  receivablesStates?: InvoiceState[];
};

type FilesQueryParams = {
  filters: FilesQueryFilters;
  isRefreshing?: boolean;
  ordersSort: OrderOption<FilesQueryOrdersSort>;
  pagination?: Pagination;
  paginationExtraOrdersQuery?: GetV1CompaniesCompanyIdOrdersDispatchData['query'];
};

const defaultOrdersSort: OrderOption<FilesQueryOrdersSort> = {
  orderBy: FilesQueryOrdersSort.DISPATCH_NUMBER_NAME,
  sortBy: 'desc',
};

type FilesQueryContextType = {
  applyFilters: (filters: Partial<FilesQueryFilters>) => void;
  applyOrdersSort: (orderOption: OrderOption<FilesQueryOrdersSort>) => void;
  applySearchQuery: (searchQuery: string) => void;
  clearAllFiltersExceptSearch: () => void;
  clearQueryData: () => void;
  data: Order[];
  // Resolves with the full, freshly-flattened order list after the next page lands, plus
  // whether a further page remains — callers that need to act on the up-to-date list (e.g.
  // cross-order navigation at the boundary of already-loaded pages) can await it and loop
  // on `hasMore` instead of re-reading stale context data or stopping after a single page.
  fetchMore: () => Promise<{ data: Order[]; hasMore: boolean }>;
  fetchReason?: FetchReason;
  filters: FilesQueryFilters;
  hasMore: boolean;
  hasUnfilteredResults: boolean;
  ordersSort: OrderOption<FilesQueryOrdersSort>;
  refresh: () => void;
  refreshCount: number;
  replaceOrderInQueryData: (order: Order) => void;
};

const FilesQueryContext = createContext<FilesQueryContextType>({
  applyFilters: noop,
  applyOrdersSort: noop,
  applySearchQuery: noop,
  clearAllFiltersExceptSearch: noop,
  clearQueryData: noop,
  data: [],
  fetchMore: () => Promise.resolve({ data: [], hasMore: false }),
  filters: {},
  hasMore: false,
  hasUnfilteredResults: false,
  ordersSort: { ...defaultOrdersSort },
  refresh: noop,
  refreshCount: 0,
  replaceOrderInQueryData: noop,
});

interface FilesQueryContextProviderProps {
  companyId: string;
  extraOrdersQuery?: GetV1CompaniesCompanyIdOrdersDispatchData['query'];
  initialOrdersSort?: OrderOption<FilesQueryOrdersSort>;
  initialSearchQueryAcrossAllDates?: Nullable<string>;
  storageNamespace?: string;
}

function FilesQueryContextProvider({
  companyId,
  children,
  extraOrdersQuery,
  initialOrdersSort = defaultOrdersSort,
  initialSearchQueryAcrossAllDates,
  storageNamespace,
}: React.PropsWithChildren<FilesQueryContextProviderProps>) {
  const queryKey = makeFilesQueryContextKey();
  const queryClient = useQueryClient();

  // defined here to avoid the "dayjs.tz is not a function" error
  const defaultFilters: FilesQueryFilters = {
    endDate: dayjs.tz(),
    startDate: dayjs.tz().subtract(7, 'day'),
  };

  const [queryParams, setQueryParams] = useState<FilesQueryParams>(() => {
    if (initialSearchQueryAcrossAllDates) {
      return {
        filters: { searchQuery: initialSearchQueryAcrossAllDates },
        ordersSort: { ...initialOrdersSort },
      };
    }

    let initialFilters: FilesQueryFilters = { ...defaultFilters };
    const storedDateRange = retrievePersistedDateRange(
      makeStorageKey(StorageKeys.APPLIED_DATE_FILTERS, storageNamespace),
    );

    if (storedDateRange) {
      initialFilters = {
        endDate: storedDateRange.endDate ? dayjs.tz(storedDateRange.endDate) : undefined,
        startDate: storedDateRange.startDate
          ? dayjs.tz(storedDateRange.startDate)
          : undefined,
      };
    }

    return {
      filters: initialFilters,
      ordersSort: { ...initialOrdersSort },
    };
  });

  // Mirrors the applied filters for reads outside the render cycle (persisting
  // merged date values without impure work inside setQueryParams updaters).
  const appliedFiltersRef = useRef(queryParams.filters);
  appliedFiltersRef.current = queryParams.filters;

  const pagination = isEqual(queryParams.paginationExtraOrdersQuery, extraOrdersQuery)
    ? queryParams.pagination
    : undefined;

  const ordersQueryKey = [
    queryKey,
    queryParams.filters,
    queryParams.ordersSort,
    extraOrdersQuery,
  ];

  const query = useInfiniteQuery({
    queryKey: ordersQueryKey,
    initialPageParam: pagination,
    getNextPageParam: () => pagination,
    queryFn: async ({ pageParam, signal }) => {
      const { filters, ordersSort } = queryParams;

      const query: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = {
        ...derivePaginationQueryParams(pageParam),
        ...deriveFilterQueryParams(filters),
        ...deriveSortQueryParam(ordersSort),
        ...extraOrdersQuery,
      };

      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      const orders = response.data.data.map((order) => Order.parse(order));
      setQueryParams((state) => ({
        ...state,
        pagination: extractPagination(response),
        paginationExtraOrdersQuery: extraOrdersQuery,
      }));

      return orders;
    },
  });

  const hasMore = Boolean(pagination?.after);

  const data = useMemo(() => (query.data?.pages ?? []).flat(), [query.data?.pages]);

  const searchQuery = queryParams.filters.searchQuery ?? '';
  const shouldProbeForUnfilteredResults =
    !query.isFetching &&
    !query.isError &&
    data.length === 0 &&
    searchQuery.trim().length > 0 &&
    hasActiveNonSearchFilters(queryParams.filters);

  // Probe = the exact query the screen would issue right after clearing filters
  // (search + default job states + the assigned narrowing, no dates), capped at one
  // row. Powers the "Clear filters and search again" hint. Failures stay silent: the
  // hint simply doesn't show.
  const unfilteredResultsProbe = useQuery({
    enabled: shouldProbeForUnfilteredResults,
    queryKey: [queryKey, 'unfiltered-probe', companyId, searchQuery, extraOrdersQuery],
    queryFn: async ({ signal }) => {
      const probeQuery: GetV1CompaniesCompanyIdOrdersDispatchData['query'] = {
        'page[limit]': 1,
        ...deriveFilterQueryParams({ searchQuery }),
        ...extraOrdersQuery,
      };

      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query: probeQuery,
        signal,
      });

      return response.data.data.length > 0;
    },
    refetchOnWindowFocus: false,
    retry: false,
    // Re-searching the same term within 30s reuses the cached probe answer
    // instead of issuing another request.
    staleTime: 30_000,
  });

  const hasUnfilteredResults = shouldProbeForUnfilteredResults
    ? (unfilteredResultsProbe.data ?? false)
    : false;

  const fetchReason = useMemo(() => {
    if (query.isFetchingNextPage) {
      return FetchReason.INFINITE_SCROLL;
    }

    if (query.isFetching) {
      return FetchReason.INITIAL_FETCH;
    }
  }, [query.isFetchingNextPage, query.isFetching]);

  const fetchMore = useCallback(async () => {
    if (!hasMore) {
      return { data, hasMore: false };
    }

    const result = await query.fetchNextPage();

    return {
      data: (result.data?.pages ?? []).flat(),
      hasMore: Boolean(result.hasNextPage),
    };
  }, [data, hasMore, query.fetchNextPage]);

  const clearQueryData = useCallback(
    (keptQueryHash?: string) => {
      queryClient.removeQueries({
        predicate(query) {
          if (query.queryHash === keptQueryHash) {
            return false;
          }

          return query.queryKey.some((key) => {
            const keyAsString = String(key);

            return (
              keyAsString.startsWith(JOBS_QUERY_CONTEXT_PREFIX) ||
              keyAsString.startsWith(queryKey)
            );
          });
        },
      });
    },
    [queryClient, queryKey],
  );

  const previousExtraOrdersQueryRef = useRef(extraOrdersQuery);
  const ordersQueryHash = hashKey(ordersQueryKey);

  useEffect(() => {
    if (isEqual(previousExtraOrdersQueryRef.current, extraOrdersQuery)) {
      return;
    }

    previousExtraOrdersQueryRef.current = extraOrdersQuery;
    // Keep the current view's orders query: it has just started, and removing it cancels
    // its read with nothing to start it again
    clearQueryData(ordersQueryHash);
  }, [clearQueryData, extraOrdersQuery, ordersQueryHash]);

  const applyFilters = useCallback<FilesQueryContextType['applyFilters']>(
    (incomingFilters) => {
      const filters =
        'jobStates' in incomingFilters
          ? {
              ...incomingFilters,
              jobStates: normalizeJobStateSelection(incomingFilters.jobStates),
            }
          : incomingFilters;
      const dateFiltersChanged = 'startDate' in filters || 'endDate' in filters;

      if (dateFiltersChanged) {
        const mergedFilters = { ...appliedFiltersRef.current, ...filters };

        persistDateRange(
          makeStorageKey(StorageKeys.APPLIED_DATE_FILTERS, storageNamespace),
          {
            endDate: mergedFilters.endDate
              ? mergedFilters.endDate.format('YYYY-MM-DD')
              : null,
            startDate: mergedFilters.startDate
              ? mergedFilters.startDate.format('YYYY-MM-DD')
              : null,
          },
        );
      }

      clearQueryData();
      setQueryParams((state) => ({
        filters: { ...state.filters, ...filters },
        ordersSort: { ...state.ordersSort },
      }));
    },
    [clearQueryData, storageNamespace],
  );

  const applySearchQuery = useCallback<FilesQueryContextType['applySearchQuery']>(
    (searchQuery) => {
      clearQueryData();

      setQueryParams((state) => {
        const isClearingSearchQuery = searchQuery.trim().length === 0;

        return {
          filters: {
            ...state.filters,
            searchQuery: isClearingSearchQuery ? undefined : searchQuery,
          },
          ordersSort: { ...state.ordersSort },
        };
      });
    },
    [clearQueryData],
  );

  const applyOrdersSort = useCallback<FilesQueryContextType['applyOrdersSort']>(
    (orderOption) => {
      clearQueryData();
      setQueryParams((state) => ({
        filters: { ...state.filters },
        ordersSort: orderOption,
      }));
    },
    [clearQueryData],
  );

  const clearAllFiltersExceptSearch = useCallback<
    FilesQueryContextType['clearAllFiltersExceptSearch']
  >(() => {
    clearQueryData();

    // The user deliberately widened to "All dates": persist explicit nulls so
    // returning to the page doesn't re-default them into the 7-day window.
    persistDateRange(makeStorageKey(StorageKeys.APPLIED_DATE_FILTERS, storageNamespace), {
      endDate: null,
      startDate: null,
    });

    // Replace (not merge): only the search term and the sort survive; dates and
    // every other filter reset, matching the probe's post-clear query.
    setQueryParams((state) => ({
      filters: { searchQuery: state.filters.searchQuery },
      ordersSort: { ...state.ordersSort },
    }));
  }, [clearQueryData, storageNamespace]);

  const [refreshCount, setRefreshCount] = useState(0);

  const refresh = useCallback<FilesQueryContextType['refresh']>(() => {
    setRefreshCount((count) => count + 1);
    setQueryParams((state) => ({
      filters: { ...state.filters },
      ordersSort: { ...state.ordersSort },
      isRefreshing: true,
    }));
  }, [clearQueryData]);

  useEffect(() => {
    if (queryParams.isRefreshing) {
      setQueryParams((state) => omit(state, 'isRefreshing'));

      clearQueryData();
      query.refetch();
    }
  }, [queryParams.isRefreshing, clearQueryData]);

  const replaceOrderInQueryData = useCallback(
    (updatedOrder: Order) => {
      queryClient.setQueriesData<{ pages: Order[][] | undefined }>(
        { queryKey: [queryKey] },
        (data) => {
          if (!data?.pages) {
            return data;
          }

          const pages = data.pages.map((page) =>
            page.map((order) => (order.id === updatedOrder.id ? updatedOrder : order)),
          );

          return { ...data, pages };
        },
      );
    },
    [queryClient, queryKey],
  );

  return (
    <FilesQueryContext.Provider
      value={{
        applyFilters,
        applyOrdersSort,
        applySearchQuery,
        clearAllFiltersExceptSearch,
        clearQueryData,
        data,
        fetchMore,
        fetchReason,
        filters: queryParams.filters,
        hasMore,
        hasUnfilteredResults,
        ordersSort: queryParams.ordersSort,
        refresh,
        refreshCount,
        replaceOrderInQueryData,
      }}
    >
      {children}
    </FilesQueryContext.Provider>
  );
}

export default FilesQueryContext;
export { FilesQueryContextProvider };
